import React, { useEffect, useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'

import { Compartment, CompartmentChangeStateEnum } from '@/entities/compartments/models/Compartment'
import { useDropdown } from '@/entities/dropdowns/libs/useDropdown'
import { runsSelectors } from '@/entities/runs'
import { copyRun } from '@/entities/runs/thunks/copyRun'
import { saveRuns } from '@/entities/runs/thunks/saveRuns'
import { toggleRunStatus } from '@/entities/runs/thunks/toggleRunStatus'
import { FatigueModelChangeStateEnum } from '@/entities/schedules/models/FatigueModel'
import { Button, Icon, IconButton, ToggleButton } from '@/shared/components'
import { useDialogContext } from '@/shared/components/Modal/DialogContext'
import { useSubscription } from '@/shared/components/Subscription/SubscriptionContext'
import { SubscriptionUpgradeDialog } from '@/shared/components/Subscription/SubscriptionUpgradeDialog'
import { useToastContext } from '@/shared/components/Toast/ToastContext'
import { timeDayjsToHhMm, timeHhMmSsToDayjs } from '@/shared/lib/timeHelper'
import { useAppDispatch, useAppSelector } from '@/shared/store'
import { pxToRem } from '@/shared/styles/themes'
import { faClose, faCopy, faEdit, faToggleOn } from '@fortawesome/pro-regular-svg-icons'
import { Box, Grid, styled, Tab, Tabs } from '@mui/material'
import { EntityId } from '@reduxjs/toolkit'

import { StyledHeader } from '../StyledOutletPage'
import { defaultRunValues } from './defaultRunValues'
import { RunFormValues } from './models/RunFormValues'
import { CompartmentsTab } from './tabs/CompartmentsTab'
import { CostsTab } from './tabs/CostsTab'
import { FatigueManagementTab } from './tabs/FatigueManagementTab'
import { GeneralTab } from './tabs/GeneralTab'
import { WorkingHoursTab } from './tabs/WorkingHoursTab'

export type RunEditFormProps = {
  runId?: EntityId
  mode?: 'single' | 'multi'
  formType?: 'add' | 'edit' | 'view'
  setFormType: React.Dispatch<React.SetStateAction<'add' | 'edit' | 'view'>>
  setRowSelectionModel: (value: EntityId | undefined) => void
  onClose?: () => void
}

export const RunEditForm: React.FC<RunEditFormProps> = ({
  runId,
  mode = 'single',
  formType = 'add',
  setFormType,
  setRowSelectionModel,
  onClose,
}) => {
  // Fetch dropdowns
  const { data: depotOptions } = useDropdown('depots')
  const { data: groupOptions } = useDropdown('groups')
  const { data: runCategoryOptions } = useDropdown('runCategories')
  const { data: colorOptions } = useDropdown('htmlColors')
  const { data: attributeOptions } = useDropdown('vehicleAttributes')
  const { data: compartmentOptions } = useDropdown('compartments')

  const [activeTab, setActiveTab] = useState(0)
  const [isSubmitting, setIsSubmitting] = useState(false)

  const { isAtVehicleLimit, refetch: refetchSubscription, isLoading: isSubscriptionLoading } = useSubscription()
  const { openDialog } = useDialogContext()
  const { setToastContent } = useToastContext()

  const dispatch = useAppDispatch()
  const isReadOnly = formType === 'view'
  const isBulkEdit = mode === 'multi'

  const run = useAppSelector(state =>
    runId ? runsSelectors.selectById(state, runId) : null,
  )

  const methods = useForm<RunFormValues>({
    defaultValues: defaultRunValues,
  })

  const { reset, handleSubmit } = methods

  const resetForm = () => {
    if (formType === 'add') {
      reset(defaultRunValues)
    } else if (run) {
      reset({
        ...run,
        groupId: groupOptions.find(opt => opt.value === run.groupId) || null,
        startDepotId: depotOptions.find(opt => opt.value === run.startDepotId) || null,
        finishDepotId: depotOptions.find(opt => opt.value === run.finishDepotId) || null,
        uiColorId: colorOptions.find(opt => opt.value === run.uiColor) || null,
        runCategoryId: runCategoryOptions.find(opt => opt.value === run.runCategoryId) || null,
        attributeIds: run.attributeIds.map(id => attributeOptions.find(opt => opt.value === id)) || [],
        compartmentDefinitionIds: run.compartmentDefinitions.map(compartment => compartmentOptions.find(opt => opt.value === compartment.id)) || [],

        loadTime: timeHhMmSsToDayjs(run.loadTime),
        unloadTime: timeHhMmSsToDayjs(run.unloadTime),
        overtimeKickIn1: timeHhMmSsToDayjs(run.overtimeKickIn1),
        overtimeKickIn2: timeHhMmSsToDayjs(run.overtimeKickIn2),
        breakDuration: timeHhMmSsToDayjs(run.breakDuration || '00:00'),
        isPaidBreaks: run.isPaidBreaks || false,
      })
    }
  }

  const onToggleFormType = () => {
    resetForm()
    setFormType((prev) => prev === 'edit' ? 'view' : 'edit')
  }

  const onCopyRun = async () => {
    // Check subscription tier vehicle limit
    if (isAtVehicleLimit()) {
      openDialog(SubscriptionUpgradeDialog, {
        feature: 'Copying Runs',
        reason: 'You have reached your run limit. Upgrade your plan to add more runs.',
        showVehicleLimit: true,
        onClose: () => { },
      })

      return
    }

    if (run) {
      await dispatch(copyRun({ runId: run.id })).unwrap().then((newRun) => {
        if (!newRun) return

        setFormType('view')
        setRowSelectionModel(newRun.id)
      })

      await refetchSubscription()
    }
  }

  const handleToggleStatus = async (id: EntityId) => {
    // Check subscription tier vehicle limit
    if (isAtVehicleLimit() && !run?.isActive) {
      openDialog(SubscriptionUpgradeDialog, {
        feature: 'Enable Run',
        reason: 'You have reached your run limit. Upgrade your plan to enable runs.',
        showVehicleLimit: true,
        onClose: () => { },
      })

      return
    }

    await dispatch(toggleRunStatus({ id }))
    await refetchSubscription()

    setToastContent({
      open: true,
      severity: 'success',
      title: 'Success',
      content: 'Depot status updated successfully',
    })
  }

  const onSubmit = async (data: RunFormValues) => {
    try {
      setIsSubmitting(true)

      // Map compartmentDefinitions back to the format required by the API
      const compartmentDefinitions: Partial<Compartment>[] = run?.compartmentDefinitions.map((compartment) => {
        const match = data.compartmentDefinitionIds.find((opt) => opt.value === compartment.id)

        if (match) {
          return {
            id: match.value,
            state: CompartmentChangeStateEnum.Unchanged,
          }
        }

        return {
          id: compartment.id,
          state: CompartmentChangeStateEnum.Deleted,
        }
      }) || []

      data.compartmentDefinitionIds.forEach((opt) => {
        const match = run?.compartmentDefinitions.find((compartment) => compartment.id === opt.value)

        if (!match) {
          compartmentDefinitions.push({
            id: opt.value,
            state: CompartmentChangeStateEnum.Added,
          })
        }
      })

      const processedFatigueModels = data.fatigueModels?.map(model => {
        if (model.state === FatigueModelChangeStateEnum.Added) {
          return { ...model, id: undefined }
        }

        return model
      }) || []
      const submitData = {
        ...data,
        groupId: data.groupId?.value,
        startDepotId: data.startDepotId?.value,
        finishDepotId: data.finishDepotId?.value,
        uiColorId: data.uiColorId?.value,
        runCategoryId: data.runCategoryId?.value,
        attributeIds: data.attributeIds.map(opt => opt.value),
        compartmentDefinitions: compartmentDefinitions,
        fatigueModels: processedFatigueModels,

        loadTime: timeDayjsToHhMm(data.loadTime),
        unloadTime: timeDayjsToHhMm(data.unloadTime),
        overtimeKickIn1: timeDayjsToHhMm(data.overtimeKickIn1),
        overtimeKickIn2: timeDayjsToHhMm(data.overtimeKickIn2),
        breakDuration: timeDayjsToHhMm(data.breakDuration),
      }

      await dispatch(saveRuns({ runs: [submitData] })).unwrap()
      setFormType('view')

      if (formType === 'add') {
        onClose?.()
      }
    } catch (error) {
      // Handle error - maybe show toast
      console.error('Failed to save run:', error)
    } finally {
      setIsSubmitting(false)
    }
  }

  // Initialize form when run changes
  useEffect(() => {
    if (!run || !['edit', 'view'].includes(formType)) return

    resetForm()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [run, formType])

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Grid container direction="row" alignItems="flex-start" justifyContent="space-between" wrap="nowrap">
          <Grid item>
            <StyledHeader variant="body1" sx={{ paddingLeft: 0 }}>
              {formType === 'add' && 'Add a new run'}
              {formType === 'edit' && `Edit ${run?.name || ''}`}
              {formType === 'view' && `View ${run?.name || ''}`}
            </StyledHeader>
          </Grid>
          <Grid item sx={{ display: 'inline-flex' }}>
            {formType !== 'add' && runId && (
              <>
                <Button
                  primary
                  outlined
                  size="small"
                  onClick={onCopyRun}
                  disabled={isSubscriptionLoading}
                  startIcon={<Icon icon={faCopy} />}
                  sx={{ marginRight: 1, minWidth: pxToRem(110) }}
                >
                  Copy Run
                </Button>

                <ToggleButton
                  value="active"
                  primary
                  text
                  selected={false}
                  onChange={() => handleToggleStatus(runId)}
                  disabled={isSubscriptionLoading}
                  sx={{ marginRight: 1, minWidth: pxToRem(120) }}
                >
                  <Icon icon={faToggleOn} sx={{ fontSize: pxToRem(14), marginRight: pxToRem(4) }} />
                  Toggle Status
                </ToggleButton>
              </>
            )}
            {formType !== 'add' && <ToggleButton
              value="checked"
              primary
              text
              selected={formType === 'edit'}
              onChange={onToggleFormType}
              sx={{ minWidth: pxToRem(100) }}
            >
              {formType !== 'edit' && <Icon icon={faEdit} sx={{ fontSize: pxToRem(14), marginRight: pxToRem(4) }} />}
              {formType === 'edit' ? 'Cancel Edit' : 'Edit Run'}
            </ToggleButton>}

            <IconButton size="small" onClick={onClose}>
              <Icon icon={faClose} sx={{ fontSize: pxToRem(18) }} />
            </IconButton>
          </Grid>
        </Grid>

        <Tabs value={activeTab} onChange={(_, value) => setActiveTab(value)}>
          <Tab label="General" disableRipple />
          <Tab label="Costs" disableRipple />
          <Tab label="Working Hours" disableRipple />
          <Tab label="Fatigue Management" disableRipple />
          <Tab label="Compartments" disableRipple />
        </Tabs>

        <StyledTabPanel display={activeTab === 0 ? 'block' : 'none'}>
          <GeneralTab
            isReadOnly={isReadOnly}
            isBulkEdit={isBulkEdit}
            depotOptions={depotOptions}
            groupOptions={groupOptions}
            colorOptions={colorOptions}
            runCategoryOptions={runCategoryOptions}
            attributeOptions={attributeOptions}
          />
        </StyledTabPanel>

        <StyledTabPanel display={activeTab === 1 ? 'block' : 'none'}>
          <CostsTab
            isReadOnly={isReadOnly}
            isBulkEdit={isBulkEdit}
          />
        </StyledTabPanel>

        <StyledTabPanel display={activeTab === 2 ? 'block' : 'none'}>
          <WorkingHoursTab
            isReadOnly={isReadOnly}
          />
        </StyledTabPanel>

        <StyledTabPanel display={activeTab === 3 ? 'block' : 'none'}>
          <FatigueManagementTab
            isReadOnly={isReadOnly}
            isBulkEdit={isBulkEdit}
            standardFatigueSettings={{
              breakDuration: timeHhMmSsToDayjs('00:15'),
              fatigueModels: [
                { id: 1, numberOfBreaks: 1, timeInterval: '05:30', state: 1 },
                { id: 2, numberOfBreaks: 2, timeInterval: '08:00', state: 1 },
                { id: 3, numberOfBreaks: 4, timeInterval: '11:00', state: 1 },
              ],
            }}
          />
        </StyledTabPanel>

        <StyledTabPanel display={activeTab === 4 ? 'block' : 'none'}>
          <CompartmentsTab
            isReadOnly={isReadOnly}
            compartmentOptions={compartmentOptions}
            attributeOptions={attributeOptions}
          />
        </StyledTabPanel>

        {formType !== 'view' && <StyledFormActions>
          <Button text onClick={onToggleFormType} disabled={isSubmitting}>Cancel</Button>
          <Button primary type="submit" disabled={isSubmitting}>
            Submit
          </Button>
        </StyledFormActions>}
      </form>
    </FormProvider>
  )
}

const StyledFormActions = styled('div')`
  position: sticky;
  bottom: ${(props) => props.theme.typography.pxToRem(-12)};
  left: 0;
  right: 0;
  z-index: 1001;
  width: 100%;
  padding-bottom: ${(props) => props.theme.typography.pxToRem(12)};
  display: flex;
  justify-content: flex-end;
  gap: ${(props) => props.theme.typography.pxToRem(12)};
  margin-top: ${(props) => props.theme.typography.pxToRem(24)};
  padding-top: ${(props) => props.theme.typography.pxToRem(8)};
  background-color: ${(props) => props.theme.palette.background.paper};
`

const StyledTabPanel = styled(Box)`
  width: 100%;
  height: calc(100% - ${(props) => props.theme.typography.pxToRem(109)});
  position: relative;
  overflow-y: auto;
  scrollbar-gutter: stable;
  
`